import React, { useEffect, useState } from 'react';
import { feedBankAPI } from '../../services/millAPI';
import { agingColor } from './bankAging';

interface Summary {
  total_available_lbs: number;
  lot_count: number;
  oldest_created_at: string | null;
  oldest_age_days: number | null;
  buckets: { fresh: number; aging: number; stale: number };
}

interface Props {
  // Click → open the Plan popup (where the per-batch confirm-pull lives).
  onOpen?: () => void;
  compact?: boolean;   // tighter styling for the Command-screen header
}

// Always-on banked-feed indicator: "Feed Bank: 6 lots · 4,200 lbs · oldest 18d".
// Colored by the oldest lot's age so a stale (red) bank can't be missed.
const BankAvailabilitySummary: React.FC<Props> = ({ onOpen, compact }) => {
  const [s, setS] = useState<Summary | null>(null);

  useEffect(() => {
    let alive = true;
    feedBankAPI.getAvailabilitySummary()
      .then(res => { if (alive) setS(res.data); })
      .catch(() => { /* indicator is non-critical; stay silent on failure */ });
    return () => { alive = false; };
  }, []);

  if (!s || s.lot_count === 0) {
    return (
      <div style={{ ...styles.box, ...(compact ? styles.compact : {}), color: '#94a3b8', borderColor: '#e5e7eb' }}>
        Feed Bank: empty
      </div>
    );
  }

  const age = s.oldest_age_days ?? 0;
  const color = agingColor(age);
  const stale = s.buckets.stale > 0;

  return (
    <div
      style={{ ...styles.box, ...(compact ? styles.compact : {}), borderColor: color, cursor: onOpen ? 'pointer' : 'default' }}
      onClick={onOpen}
      title="Banked feed available — click to plan & confirm pulls"
    >
      <span style={{ fontWeight: 700 }}>Feed Bank:</span>{' '}
      <span>{s.lot_count} lot{s.lot_count !== 1 ? 's' : ''}</span>
      {' · '}
      <span>{s.total_available_lbs.toLocaleString()} lbs</span>
      {' · '}
      <span style={{ color, fontWeight: 700 }}>oldest {age}d</span>
      {stale && <span style={styles.staleTag}>⚠ {s.buckets.stale} stale</span>}
    </div>
  );
};

const styles: { [k: string]: React.CSSProperties } = {
  box: {
    display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap',
    border: '1px solid', borderRadius: 6, padding: '6px 12px',
    background: '#fff', fontSize: 13.5,
  },
  compact: { padding: '4px 10px', fontSize: 13 },
  staleTag: {
    marginLeft: 6, color: '#b91c1c', fontWeight: 700, fontSize: 12,
    background: '#fef2f2', borderRadius: 4, padding: '1px 6px',
  },
};

export default BankAvailabilitySummary;
